// CloudSaveManager - Syncs local saves to Firebase Cloud
// Handles throttled uploads, conflict resolution, and profile updates

import {
    doc,
    getDoc,
    setDoc,
    deleteDoc,
    serverTimestamp
} from 'https://www.gstatic.com/firebasejs/10.7.1/firebase-firestore.js';
import { db, isConfigured } from './config.js';

export class CloudSaveManager {
    constructor(accountManager) {
        this.accountManager = accountManager;
        this.lastCloudSync = 0;
        this.syncThrottleMs = 5 * 60 * 1000; // 5 MINUTES between cloud syncs (saves Firebase writes)
        this.pendingSync = null;
        this.isSyncing = false;
        this.syncListeners = [];
    }

    /**
     * Register a callback for sync status changes
     */
    onSyncStatusChanged(callback) {
        this.syncListeners.push(callback);
        return () => {
            const index = this.syncListeners.indexOf(callback);
            if (index > -1) this.syncListeners.splice(index, 1);
        };
    }

    /**
     * Notify listeners of sync status
     */
    _notifySyncStatus(status, error = null) {
        this.syncListeners.forEach(cb => {
            try {
                cb({ status, error, lastSync: this.lastCloudSync });
            } catch (e) {
                console.error('[CloudSaveManager] Listener error:', e);
            }
        });
    }

    /**
     * Queue a sync (called by SaveManager callback)
     * @param {object} state - Game state to sync
     * @param {boolean} force - Force immediate sync
     */
    queueSync(state, force = false) {
        if (!isConfigured || !this.accountManager.getUserId()) {
            return;
        }

        this.pendingSync = state;

        // Check throttle
        const now = Date.now();
        if (!force && now - this.lastCloudSync < this.syncThrottleMs) {
            // Will sync later when throttle expires
            this._scheduleDelayedSync();
            return;
        }

        this._doSync(state);
    }

    /**
     * Schedule a delayed sync for throttled saves
     */
    _scheduleDelayedSync() {
        if (this._delayedSyncTimer) return; // Already scheduled

        const delay = this.syncThrottleMs - (Date.now() - this.lastCloudSync);
        this._delayedSyncTimer = setTimeout(() => {
            this._delayedSyncTimer = null;
            if (this.pendingSync) {
                this._doSync(this.pendingSync);
            }
        }, delay);
    }

    /**
     * Perform the actual sync to cloud
     * Batches: cloud save + profile update + session time in ONE operation
     */
    async _doSync(state) {
        if (this.isSyncing || !db) return;

        const userId = this.accountManager.getUserId();
        if (!userId) return;

        // IMPORTANT: Don't sync if this save belongs to a different user
        const saveOwnerId = state._cloudSaveOwnerId;
        if (saveOwnerId && saveOwnerId !== userId) {
            console.warn('[CloudSaveManager] Refusing to sync - save belongs to different user');
            return;
        }

        this.isSyncing = true;
        this._notifySyncStatus('syncing');

        try {
            // Tag the state with the owner ID
            const stateWithOwner = { ...state, _cloudSaveOwnerId: userId };

            const saveRef = doc(db, 'users', userId, 'saves', 'current');

            // Cloud save (leaderboards are now server-side, no profile update needed)
            // Use merge: true to preserve server-managed fields like periodTracking
            await setDoc(saveRef, {
                version: 1,
                savedAt: serverTimestamp(),
                checksum: this._calculateChecksum(stateWithOwner),
                state: this._sanitizeStateForCloud(stateWithOwner)
            }, { merge: true });

            this.lastCloudSync = Date.now();
            this.pendingSync = null;
            this._notifySyncStatus('synced');
            console.log('[CloudSaveManager] Synced to cloud');
        } catch (error) {
            console.error('[CloudSaveManager] Sync failed:', error);
            this._notifySyncStatus('error', error);
        } finally {
            this.isSyncing = false;
        }
    }

    /**
     * Load save from cloud
     * @returns {object|null} Cloud save state or null
     */
    async loadFromCloud() {
        const result = await this.loadFromCloudWithMeta();
        return result ? result.state : null;
    }

    /**
     * Load save from cloud with metadata (timestamp)
     * @returns {{state: object, savedAt: number}|null} Cloud save with metadata or null
     */
    async loadFromCloudWithMeta() {
        if (!isConfigured || !db) return null;

        const userId = this.accountManager.getUserId();
        if (!userId) return null;

        try {
            const saveRef = doc(db, 'users', userId, 'saves', 'current');
            const snapshot = await getDoc(saveRef);

            if (!snapshot.exists()) {
                console.log('[CloudSaveManager] No cloud save found');
                return null;
            }

            const data = snapshot.data();
            console.log('[CloudSaveManager] Loaded cloud save');
            return {
                state: data.state,
                savedAt: data.savedAt?.toMillis?.() || data.savedAt || 0
            };
        } catch (error) {
            console.error('[CloudSaveManager] Load failed:', error);
            return null;
        }
    }

    /**
     * Sync with cloud - handles conflict resolution
     * @param {object} localState - Current local state
     * @returns {'local'|'cloud'|'conflict'|'none'} Which state won
     */
    async syncWithCloud(localState) {
        if (!isConfigured || !db) return 'none';

        await this.accountManager.ensureSignedIn();
        const userId = this.accountManager.getUserId();
        if (!userId) return 'none';

        const cloudData = await this.loadFromCloudWithMeta();
        const cloudState = cloudData?.state;

        // Debug logging for sync decisions
        console.log('[CloudSaveManager] syncWithCloud:', {
            hasLocal: !!localState,
            hasCloud: !!cloudState,
            localLifetimeMiles: localState?.lifetimeMiles,
            cloudLifetimeMiles: cloudState?.lifetimeMiles,
            localOwnerId: localState?._cloudSaveOwnerId,
            userId: userId
        });

        // Check if local save belongs to a different user
        const localOwnerId = localState?._cloudSaveOwnerId;
        const localBelongsToDifferentUser = localOwnerId && localOwnerId !== userId;

        if (localBelongsToDifferentUser) {
            console.log('[CloudSaveManager] Local save belongs to different user, preferring cloud');
            // If cloud exists, use it. Otherwise start fresh (don't use other user's data)
            if (cloudState) {
                return 'cloud';
            }
            return 'none'; // Start fresh, don't use other user's save
        }

        // No cloud save - push local (if local exists and belongs to this user)
        if (!cloudState && localState && !localBelongsToDifferentUser) {
            console.log('[CloudSaveManager] No cloud save, pushing local');
            await this._doSync(localState);
            return 'local';
        }

        // No local save - use cloud
        if (!localState && cloudState) {
            console.log('[CloudSaveManager] No local save, using cloud');
            return 'cloud';
        }

        // Neither exists
        if (!localState && !cloudState) {
            console.log('[CloudSaveManager] Neither local nor cloud exists');
            return 'none';
        }

        // Both exist - check if they're essentially the same
        const localMiles = localState.lifetimeMiles ?? localState.totalMiles ?? 0;
        const cloudMiles = cloudState.lifetimeMiles ?? cloudState.totalMiles ?? 0;
        const cloudTime = cloudData.savedAt || 0;
        const localTime = localState._lastSaveTime || Date.now();

        console.log('[CloudSaveManager] === SYNC COMPARISON ===');
        console.log('[CloudSaveManager] Local:', { miles: localMiles, time: new Date(localTime).toISOString() });
        console.log('[CloudSaveManager] Cloud:', { miles: cloudMiles, time: new Date(cloudTime).toISOString() });

        // If miles are the same (within small tolerance), no conflict - just use local and sync
        const milesDiff = Math.abs(localMiles - cloudMiles);
        if (milesDiff < 0.01) {
            console.log('[CloudSaveManager] Saves are identical, using local silently');
            await this._doSync(localState);
            return 'local';
        }

        // Real conflict - show modal so user can choose
        console.log('[CloudSaveManager] Both saves exist with different progress - showing conflict modal');
        return 'conflict';
    }

    /**
     * Get the cloud state for conflict resolution
     */
    async getCloudStateForConflict() {
        return await this.loadFromCloud();
    }

    /**
     * Force push local state to cloud (for conflict resolution, logout, close)
     */
    async forceLocalToCloud(state) {
        console.log('[CloudSaveManager] forceLocalToCloud:', {
            totalMiles: state?.totalMiles
        });
        // Bypass throttle - sync immediately
        this.lastCloudSync = 0;
        await this._doSync(state);
    }

    /**
     * Delete cloud save data (for New Game when logged in)
     */
    async deleteFromCloud() {
        if (!isConfigured || !db) return;

        const userId = this.accountManager.getUserId();
        if (!userId) return;

        try {
            const saveRef = doc(db, 'users', userId, 'saves', 'current');
            await deleteDoc(saveRef);
            console.log('[CloudSaveManager] Deleted cloud save');
        } catch (error) {
            console.error('[CloudSaveManager] Failed to delete cloud save:', error);
            throw error;
        }
    }

    // NOTE: Leaderboards are now server-side. The Cloud Function
    // updateLeaderboards polls user saves every 60s and updates
    // leaderboards/alltime, leaderboards/{weekId}, leaderboards/{dayId}

    /**
     * Sanitize state for cloud storage (remove any sensitive or oversized data)
     */
    _sanitizeStateForCloud(state) {
        // Create a copy to avoid mutating original
        const sanitized = JSON.parse(JSON.stringify(state));

        // Remove any fields that shouldn't go to cloud
        // (Currently none, but this is where you'd filter)

        return sanitized;
    }

    /**
     * Calculate checksum for integrity validation
     */
    _calculateChecksum(state) {
        const str = JSON.stringify(state);
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            hash = ((hash << 5) - hash) + str.charCodeAt(i);
            hash |= 0;
        }
        return hash.toString(16);
    }

    /**
     * Get last sync time
     */
    getLastSyncTime() {
        return this.lastCloudSync;
    }

    /**
     * Check if there's a pending sync
     */
    hasPendingSync() {
        return this.pendingSync !== null;
    }

    /**
     * Cleanup
     */
    dispose() {
        if (this._delayedSyncTimer) {
            clearTimeout(this._delayedSyncTimer);
        }
        this.syncListeners = [];
    }

    // =========================================================================
    // MANUAL BACKUP SLOT
    // =========================================================================

    /**
     * Save current state to manual backup slot
     * @param {object} state - Game state to backup
     * @returns {Promise<boolean>} Success
     */
    async saveToBackup(state) {
        if (!isConfigured || !db) return false;

        const userId = this.accountManager.getUserId();
        if (!userId) return false;

        try {
            const stateWithOwner = { ...state, _cloudSaveOwnerId: userId };
            const backupRef = doc(db, 'users', userId, 'saves', 'backup');

            await setDoc(backupRef, {
                version: 1,
                savedAt: serverTimestamp(),
                checksum: this._calculateChecksum(stateWithOwner),
                state: this._sanitizeStateForCloud(stateWithOwner),
                // Top-level for quick metadata access
                lifetimeMiles: state.lifetimeMiles ?? state.totalMiles ?? 0,
                prestigeLevel: state.prestige?.level ?? 0
            });

            console.log('[CloudSaveManager] Saved to backup slot');
            return true;
        } catch (error) {
            console.error('[CloudSaveManager] Backup save failed:', error);
            throw error;
        }
    }

    /**
     * Get backup metadata without loading full state
     * @returns {Promise<{savedAt: number, lifetimeMiles: number, prestigeLevel: number}|null>}
     */
    async getBackupInfo() {
        if (!isConfigured || !db) return null;

        const userId = this.accountManager.getUserId();
        if (!userId) return null;

        try {
            const backupRef = doc(db, 'users', userId, 'saves', 'backup');
            const snapshot = await getDoc(backupRef);

            if (!snapshot.exists()) {
                return null;
            }

            const data = snapshot.data();
            return {
                savedAt: data.savedAt?.toMillis?.() || data.savedAt || 0,
                lifetimeMiles: data.lifetimeMiles ?? data.state?.lifetimeMiles ?? 0,
                prestigeLevel: data.prestigeLevel ?? data.state?.prestige?.level ?? 0
            };
        } catch (error) {
            console.error('[CloudSaveManager] Get backup info failed:', error);
            return null;
        }
    }

    /**
     * Load full state from backup slot
     * @returns {Promise<{state: object, savedAt: number}|null>}
     */
    async loadFromBackup() {
        if (!isConfigured || !db) return null;

        const userId = this.accountManager.getUserId();
        if (!userId) return null;

        try {
            const backupRef = doc(db, 'users', userId, 'saves', 'backup');
            const snapshot = await getDoc(backupRef);

            if (!snapshot.exists()) {
                console.log('[CloudSaveManager] No backup found');
                return null;
            }

            const data = snapshot.data();
            console.log('[CloudSaveManager] Loaded from backup slot');
            return {
                state: data.state,
                savedAt: data.savedAt?.toMillis?.() || data.savedAt || 0
            };
        } catch (error) {
            console.error('[CloudSaveManager] Backup load failed:', error);
            return null;
        }
    }

    /**
     * Delete backup slot
     * @returns {Promise<boolean>}
     */
    async deleteBackup() {
        if (!isConfigured || !db) return false;

        const userId = this.accountManager.getUserId();
        if (!userId) return false;

        try {
            const backupRef = doc(db, 'users', userId, 'saves', 'backup');
            await deleteDoc(backupRef);
            console.log('[CloudSaveManager] Deleted backup');
            return true;
        } catch (error) {
            console.error('[CloudSaveManager] Delete backup failed:', error);
            return false;
        }
    }
}
